import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "../components/Logo";
import { EditWebsite } from "../components/EditWebsite";
import { imgUrl } from "../components/Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-none border-white/20 bg-transparent text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-turquoise";

const formatError = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [upload, setUpload] = useState({ title: "", category: "Completed Build", file: null });
  const [uploading, setUploading] = useState(false);
  const [transform, setTransform] = useState({ title: "", before: null, after: null });
  const [transforming, setTransforming] = useState(false);

  const full = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const loadData = useCallback(async () => {
    try {
      const [q, m, g, t] = await Promise.all([
        axios.get(`${API}/quotes`, { withCredentials: true }),
        axios.get(`${API}/contact`, { withCredentials: true }),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/transformations`),
      ]);
      setQuotes(q.data);
      setMessages(m.data);
      setGallery(g.data);
      setTransformations(t.data);
    } catch {
      toast.error("Could not load dashboard data.");
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!upload.file || !upload.title) {
      toast.error("Add a title and choose a photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", upload.title);
      fd.append("category", upload.category);
      fd.append("file", upload.file);
      await axios.post(`${API}/gallery`, fd, { withCredentials: true });
      toast.success("Photo added to the gallery.");
      setUpload({ title: "", category: "Completed Build", file: null });
      e.target.reset();
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, { withCredentials: true });
      toast.success("Photo removed.");
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const doUploadTransform = async (e) => {
    e.preventDefault();
    if (!transform.title || !transform.before || !transform.after) {
      toast.error("Add a title and choose both a before and an after photo.");
      return;
    }
    setTransforming(true);
    try {
      const fd = new FormData();
      fd.append("title", transform.title);
      fd.append("before", transform.before);
      fd.append("after", transform.after);
      await axios.post(`${API}/transformations`, fd, { withCredentials: true });
      toast.success("Before & after pair added.");
      setTransform({ title: "", before: null, after: null });
      e.target.reset();
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setTransforming(false);
    }
  };

  const doDeleteTransform = async (id) => {
    try {
      await axios.delete(`${API}/transformations/${id}`, { withCredentials: true });
      toast.success("Transformation removed.");
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian text-white/40">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-5" data-testid="admin-login-page">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-surface/60 p-8">
          <div className="mb-8 flex flex-col items-center gap-4">
            <Logo className="h-20 w-20" />
            <p className="font-heading text-sm font-bold uppercase tracking-[0.3em] text-white">Owner Login</p>
          </div>
          <Input
            data-testid="admin-email-input"
            type="email"
            required
            placeholder="Email"
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className={inputCls}
          />
          <Input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className={`${inputCls} mt-4`}
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-6 w-full bg-volt px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian px-5 py-8 text-white sm:px-8 lg:px-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
        <div className="flex items-center gap-4">
          <Logo className="h-14 w-14" />
          <div>
            <p className="font-heading text-lg font-bold tracking-[0.15em]">ZEKE INDUSTRIES</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Owner Dashboard</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={loadData}
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-turquoise hover:text-turquoise"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-red-400 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Quote Requests", value: quotes.length },
          { label: "Messages", value: messages.length },
          { label: "Gallery Photos", value: gallery.length },
        ].map((s) => (
          <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="border border-white/10 bg-surface/60 p-6">
            <p className="font-heading text-4xl font-black text-volt">{s.value}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/50">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="quotes" className="mt-10">
        <TabsList className="rounded-none border border-white/10 bg-surface/60">
          <TabsTrigger data-testid="admin-tab-quotes" value="quotes" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black">Quotes</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-messages" value="messages" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black">Messages</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-gallery" value="gallery" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black">Gallery</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-transformations" value="transformations" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black">Before &amp; After</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-website" value="website" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black">Edit Website</TabsTrigger>
        </TabsList>

        <TabsContent value="quotes" className="mt-6 space-y-4" data-testid="admin-quotes-list">
          {quotes.length === 0 && <p className="border border-white/10 p-8 text-center text-sm text-white/40">No quote requests yet.</p>}
          {quotes.map((q) => (
            <div key={q.id} data-testid={`admin-quote-${q.id}`} className="border border-white/10 bg-surface/60 p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-heading text-lg font-bold">{q.name} <span className="text-white/40">— {q.building_type}</span></p>
                <p className="text-xs text-white/40">{fmtDate(q.created_at)}</p>
              </div>
              <p className="mt-2 text-sm text-white/70">
                {q.width}′ × {q.length}′ × {q.height}′{q.roof_pitch ? ` · Roof: ${q.roof_pitch}` : ""} · {q.email}{q.phone ? ` · ${q.phone}` : ""}
              </p>
              {q.options?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {q.options.map((o, i) => (
                    <span key={i} className="border border-turquoise/40 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-turquoise">
                      {typeof o === "string" ? o : `${o.name}${o.quantity > 1 ? ` ×${o.quantity}` : ""}${o.size ? ` (${o.size})` : ""}`}
                    </span>
                  ))}
                </div>
              )}
              {q.notes && <p className="mt-3 text-sm italic text-white/50">"{q.notes}"</p>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="messages" className="mt-6 space-y-4" data-testid="admin-messages-list">
          {messages.length === 0 && <p className="border border-white/10 p-8 text-center text-sm text-white/40">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-white/10 bg-surface/60 p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-heading text-lg font-bold">{m.name}</p>
                <p className="text-xs text-white/40">{fmtDate(m.created_at)}</p>
              </div>
              <p className="mt-1 text-sm text-turquoise">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
              <p className="mt-3 text-sm leading-relaxed text-white/70">{m.message}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="gallery" className="mt-6" data-testid="admin-gallery-panel">
          <form onSubmit={doUpload} data-testid="admin-upload-form" className="border border-white/10 bg-surface/60 p-6 sm:p-8">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em]">Add a project photo</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                data-testid="admin-upload-title-input"
                placeholder='Title (e.g. "Smith Shop — 40×60")'
                value={upload.title}
                onChange={(e) => setUpload({ ...upload, title: e.target.value })}
                className={inputCls}
              />
              <Select value={upload.category} onValueChange={(v) => setUpload({ ...upload, category: v })}>
                <SelectTrigger data-testid="admin-upload-category-select" className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/20 bg-surface text-white">
                  {["Completed Build", "Erection In Progress", "Residential", "Commercial"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid="admin-upload-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setUpload({ ...upload, file: e.target.files?.[0] || null })}
                className={`${inputCls} cursor-pointer py-2.5 file:mr-3 file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-xs file:text-white`}
              />
            </div>
            <button
              data-testid="admin-upload-submit"
              type="submit"
              disabled={uploading}
              className="mt-5 flex items-center gap-2 bg-volt px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-white disabled:opacity-50"
            >
              <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload Photo"}
            </button>
          </form>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((item) => (
              <figure key={item.id} data-testid={`admin-gallery-item-${item.id}`} className="group relative">
                <div className="aspect-[4/3] overflow-hidden border border-white/10">
                  <img src={imgUrl(item)} alt={item.title} className="h-full w-full object-cover" />
                </div>
                <figcaption className="mt-2 flex items-center justify-between gap-3">
                  <span className="truncate text-sm font-bold uppercase tracking-wide">{item.title}</span>
                  <button
                    data-testid={`admin-delete-${item.id}`}
                    onClick={() => doDelete(item.id)}
                    className="shrink-0 border border-white/20 p-2 text-white/50 transition-colors hover:border-red-400 hover:text-red-400"
                    aria-label="Delete photo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </figcaption>
              </figure>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transformations" className="mt-6" data-testid="admin-transformations-panel">
          <form onSubmit={doUploadTransform} data-testid="admin-transform-form" className="border border-white/10 bg-surface/60 p-6 sm:p-8">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em]">Add a before &amp; after pair</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                data-testid="admin-transform-title-input"
                placeholder='Title (e.g. "Smith Shop — 40×60")'
                value={transform.title}
                onChange={(e) => setTransform({ ...transform, title: e.target.value })}
                className={inputCls}
              />
              <Input
                data-testid="admin-transform-before-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setTransform({ ...transform, before: e.target.files?.[0] || null })}
                className={`${inputCls} cursor-pointer py-2.5 file:mr-3 file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-xs file:text-white`}
              />
              <Input
                data-testid="admin-transform-after-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setTransform({ ...transform, after: e.target.files?.[0] || null })}
                className={`${inputCls} cursor-pointer py-2.5 file:mr-3 file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-xs file:text-white`}
              />
            </div>
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
              First file = before (raising day) · Second file = after (finished build)
            </p>
            <button
              data-testid="admin-transform-submit"
              type="submit"
              disabled={transforming}
              className="mt-5 flex items-center gap-2 bg-volt px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-white disabled:opacity-50"
            >
              <Upload className="h-4 w-4" /> {transforming ? "Uploading..." : "Upload Pair"}
            </button>
          </form>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {transformations.map((t) => (
              <figure key={t.id} data-testid={`admin-transform-${t.id}`}>
                <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
                  <img src={full(t.before_url)} alt={`${t.title} before`} className="aspect-video w-full object-cover" />
                  <img src={full(t.after_url)} alt={`${t.title} after`} className="aspect-video w-full object-cover" />
                </div>
                <figcaption className="mt-2 flex items-center justify-between gap-3">
                  <span className="truncate text-sm font-bold uppercase tracking-wide">{t.title}</span>
                  <button
                    data-testid={`admin-transform-delete-${t.id}`}
                    onClick={() => doDeleteTransform(t.id)}
                    className="shrink-0 border border-white/20 p-2 text-white/50 transition-colors hover:border-red-400 hover:text-red-400"
                    aria-label="Delete transformation"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </figcaption>
              </figure>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="website" className="mt-6" data-testid="admin-website-panel">
          <EditWebsite />
        </TabsContent>
      </Tabs>
    </div>
  );
}
